import React, { useRef } from 'react';
import { Menu, MenuProps } from '@mui/material';

import { useControlled } from '@mui/material/utils';

export type FixedMenuProps = Omit<
  MenuProps,
  'open' | 'anchorEl' | 'anchorReference' | 'onClose'
> & {
  trigger: React.ReactElement;
  id: string;
  open?: boolean;
  onClose?: (event: React.SyntheticEvent | Event) => void;
};

// This component wraps the default menu component and uses the "anchorPosition" as an anchor reference
const FixedMenu = React.memo(
  ({
    children,
    id,
    trigger,
    open: controlledOpen,
    onClose,
    className,
    slotProps,
    ...props
  }: FixedMenuProps) => {
    const anchorRef = useRef<HTMLButtonElement>(null);

    const [open, setOpen] = useControlled({
      controlled: controlledOpen,
      default: false,
      name: 'FixedMenu',
      state: 'open',
    });

    const anchorPositionRef = useRef({
      top: 0,
      left: 0,
    });

    const { onClick: triggerClick, id: triggerId } =
      typeof trigger !== 'function' ? React.Children.only(trigger).props : ({} as any);

    const handleOpen = () => {
      if (anchorRef.current) {
        const anchorPosition = anchorRef.current.getBoundingClientRect();
        anchorPositionRef.current = {
          top: anchorPosition.bottom,
          left: anchorPosition.left,
        };

        setOpen(true);
      }

      triggerClick?.();
    };

    const handleClose = (event: React.SyntheticEvent | Event) => {
      setOpen(false);

      anchorPositionRef.current = {
        top: 0,
        left: 0,
      };

      onClose?.(event);
    };

    const triggerProps = {
      'aria-haspopup': true,
      'aria-controls': open ? id : undefined,
      'aria-expanded': open ? true : undefined,
      ref: anchorRef,
      onClick: handleOpen,
    };

    return (
      <>
        {React.cloneElement(trigger, triggerProps)}
        <Menu
          key={open ? 'opened' : 'closed'}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          {...props}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: anchorPositionRef.current.top,
            left: anchorPositionRef.current.left,
          }}
          id={id}
          open={open}
          onClose={handleClose}
          slotProps={{
            ...slotProps,
            list: {
              'aria-labelledby': triggerId,
              ...slotProps?.list,
            },
          }}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement, {
                onClick: (event: React.SyntheticEvent | Event) => {
                  handleClose(event);
                  child.props.onClick?.(event);
                },
              });
            }

            return child;
          })}
        </Menu>
      </>
    );
  }
);

export default FixedMenu;
